import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { AlertPopup, BASE_URI, HttpLoader } from 'app/app.config';
import * as _ from 'underscore';
import { UserService } from '../user/user.service';
import { UserComponent } from '../user/user.component';
import { saveAs as importedSaveAs } from 'file-saver';

export interface UploadFile {
  file: File;
  id: string;
  sizeLabel: string;
  preview?: string; // dataURL for images
  error?: string;
}

@Component({
  selector: 'app-bulk-upload',
  // standalone: true,
  // imports: [],
  templateUrl: './bulk-upload.component.html',
  styleUrl: './bulk-upload.component.scss'
})
export class BulkUploadComponent {

  @Input() isOpen = false;
  @Input() accept = '.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,image/*';
  @Input() maxFiles = 20;
  @Input() maxFileSizeMB = 10; // single-file limit
  @Output() close = new EventEmitter<void>();
  @Output() filesSelected = new EventEmitter<UploadFile[]>();

  @ViewChild('excelFile') excelFile: ElementRef;

  files: UploadFile[] = [];
  dragging = false;
  excelFileList: any;

  userCreationViewCtrl = {
    addUserBtn: false,
    showForm: true,
    showTable: false,
    showFileChoosen: false,
    startUpload: false
  };

  excelFileOpt: any = {
    percent: 0,
    showProgress: false
  };

  excelUserList: any = [];
  bulkUserFileName: string;

  // helper
  private uid() { return Math.random().toString(36).slice(2, 9); }

  constructor(
    private http: HttpClient,
    private alert: AlertPopup,
    private httpLoader: HttpLoader,
    private userService: UserService,
    private userComponent: UserComponent
  ) {

  }

  onFileInputChange(event: any) {
    // const files = event.target.files[0];
    // if (!_.isObject(files)) {
    //   return;
    // }
    // this.excelFileList = files;
  }

  onExcelFileChoosen(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    // Convert FileList to array
    const filesArray = Array.from(input.files);

    const files = input.files;
    if (!_.isObject(files)) {
      return;
    }
    this.excelFileList = files[0];

    this.addFiles(filesArray);
  }

  onDrop(ev: DragEvent) {
    ev.preventDefault();
    this.dragging = false;
    if (!ev.dataTransfer) return;
    const dtFiles = Array.from(ev.dataTransfer.files);
    console.log(dtFiles, 'Files');

    this.addFiles(dtFiles);
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEsc() {
    if (this.isOpen) this.doClose();
  }

  onDragOver(ev: DragEvent) {
    ev.preventDefault();
    this.dragging = true;
  }

  onDragLeave(ev?: DragEvent) {
    this.dragging = false;
  }

  addFiles(list: File[]) {
    if (!list.length) return;
    const allowedToAdd = this.maxFiles - this.files.length;
    if (allowedToAdd <= 0) return;

    const toAdd = list.slice(0, allowedToAdd);
    toAdd.forEach(f => {
      const fileMeta: UploadFile = {
        id: this.uid(),
        file: f,
        sizeLabel: this.humanFileSize(f.size)
      };

      // validation
      if (this.maxFileSizeMB && f.size > this.maxFileSizeMB * 1024 * 1024) {
        fileMeta.error = `File too large (max ${this.maxFileSizeMB} MB)`;
      } else if (this.accept && !this.fileMatchesAccept(f)) {
        fileMeta.error = `Unsupported file type`;
      } else {
        // preview images
        if (f.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = () => fileMeta.preview = String(reader.result);
          reader.readAsDataURL(f);
        }
      }

      this.files.push(fileMeta);
    });

    // small debounce to let previews load before emit; parent can start upload later
    this.filesSelected.emit(this.files);
  }

  removeFile(id: string) {
    this.files = this.files.filter(f => f.id !== id);
    this.filesSelected.emit(this.files);
  }

  clearAll() {
    this.files = [];
    this.excelFileList = null;
    if (this.excelFile && this.excelFile.nativeElement) {
      this.excelFile.nativeElement.value = null;
    }
    this.filesSelected.emit(this.files);
  }

  submit() {
    // only emit non-error files
    const valid = this.files.filter(f => !f.error);
    this.filesSelected.emit(valid);
    // prefer parent to start upload. Auto-close optional:
    this.doClose();
  }

  doClose() {
    this.isOpen = false;
    if (this.excelFile && this.excelFile.nativeElement) {
      this.excelFile.nativeElement.value = null;
    }
    this.excelFileList = null;
    this.files = [];
    // Reset view controller to initial state
    this.userCreationViewCtrl = {
      addUserBtn: false,
      showForm: true,
      showTable: false,
      showFileChoosen: false,
      startUpload: false
    };
    this.excelUserList = [];
    this.excelFileOpt = {
      percent: 0,
      showProgress: false
    };
    this.close.emit();
  }

  private fileMatchesAccept(file: File) {
    if (!this.accept) return true;
    const acceptList = this.accept.split(',').map(s => s.trim());
    // simple matching: accept by mime or extension
    for (const a of acceptList) {
      if (a === '*') return true;
      if (a.includes('/') && file.type === a) return true;
      if (a.endsWith('/*')) {
        const prefix = a.split('/')[0];
        if (file.type.startsWith(prefix + '/')) return true;
      }
      if (a.startsWith('.')) {
        if (file.name.toLowerCase().endsWith(a.toLowerCase())) return true;
      }
    }
    return false;
  }

  private humanFileSize(bytes: number) {
    const thresh = 1024;
    if (Math.abs(bytes) < thresh) return bytes + ' B';
    const units = ['KB', 'MB', 'GB', 'TB'];
    let u = -1;
    do {
      bytes /= thresh;
      ++u;
    } while (Math.abs(bytes) >= thresh && u < units.length - 1);
    return bytes.toFixed(1) + ' ' + units[u];
  }

  onUploadFileProcess(): void {
    if (!this.excelFileList) {
      this.alert.showAlertMessage('Please select a valid Excel file before uploading.');
      return;
    }

    const URL = `${BASE_URI.URL}/users/xls-file-parser`;
    const formData = new FormData();
    formData.append('file', this.excelFileList);
    console.log("API CHange");
    const req = new HttpRequest('POST', URL, formData, {
      reportProgress: true,
      responseType: 'json'
    });

    // Initialize progress UI
    this.excelFileOpt.showProgress = true;
    this.excelFileOpt.percent = 0;

    this.http.request(req).subscribe({
      next: (event: any) => {
        switch (event.type) {
          case HttpEventType.Sent:
            console.log('Upload started...');
            break;

          case HttpEventType.UploadProgress:
            if (event.total) {
              this.excelFileOpt.percent = Math.round((100 * event.loaded) / event.total);
            }
            break;

          case HttpEventType.Response:
            this.excelFileOpt.showProgress = false;
            this.excelFileOpt.percent = 0;
            this.excelFileList = null;

            // Switch to preview view
            this.userCreationViewCtrl = {
              ...this.userCreationViewCtrl,
              addUserBtn: false,
              showForm: false,
              showFileChoosen: false,
              showTable: true
            };

            const body = event.body;
            this.excelUserList = body?.userList || [];
            this.bulkUserFileName = body?.fileName || '';

            if (this.excelUserList.length === 0) {
              this.alert.showAlertMessage('No valid users found in uploaded file.');
            }
            break;
        }
      },
      error: (err) => {
        console.error('Upload failed:', err);
        this.excelFileOpt.showProgress = false;
        this.excelFileOpt.percent = 0;

        let errorMessage = 'File upload failed. Please try again.';
        
        // Handle different error response formats
        if (err?.error) {
          if (err.error instanceof Blob) {
            // Handle blob response (e.g., error responses as blobs)
            err.error.text().then((text: string) => {
              try {
                const errorObj = JSON.parse(text);
                errorMessage = errorObj.message || errorObj.error || errorMessage;
                this.alert.showAlertMessage({ message: errorMessage });
              } catch {
                this.alert.showAlertMessage({ message: errorMessage });
              }
            }).catch(() => {
              this.alert.showAlertMessage({ message: errorMessage });
            });
            return;
          } else if (typeof err.error === 'string') {
            errorMessage = err.error;
          } else if (err.error?.message) {
            errorMessage = err.error.message;
          } else if (err.error?.error) {
            errorMessage = err.error.error;
          } else if (_.isObject(err.error)) {
            // Try to extract message from error object
            errorMessage = err.error.message || err.error.error || JSON.stringify(err.error);
          }
        } else if (err?.message) {
          errorMessage = err.message;
        }

        // Format as object for AlertPopup
        this.alert.showAlertMessage({ message: errorMessage });
      }
    });
  }

  cancelUserList() {
    this.excelFileUploadSectionHandler();
  }

  removeUserFromListHandler(user) {
    if (user) {
      const userListCopy = [...this.excelUserList];
      const filteredUserList = userListCopy.filter(
        el => el.email_id !== user.email_id
      );
      this.excelUserList = filteredUserList;

      if (this.excelUserList.length === 0) {
        this.excelFileUploadSectionHandler();
      }
    }
  }

  createBulkUser() {
    const payLoad = this.excelUserList;
    if (!_.isArray(payLoad)) {
      return;
    }
    this.userCreationViewCtrl.startUpload = true;
    this.userService.bulkUserCreate(payLoad).subscribe(
      response => {
        const userList = [
          ...this.userComponent.listOfUsers,
          ...response['createdResult']
        ];
        // this.userCreationViewCtrl.startUpload = false;
        //this.listOfUsers = userList;
        this.alert.showAlertMessage(response);
        this.excelUserList = [];
        this.onCancelFileUploadSection();
        this.userComponent.getUserListSortAndSearch(0);
        // this.selectedTableName = "user";
        this.doClose();
      },
      error => {
        this.userCreationViewCtrl.startUpload = false;
        this.alert.showAlertMessage(error.error);
      }
    );
  }

  excelFileUploadSectionHandler() {
    this.userCreationViewCtrl.addUserBtn = true;
    this.userCreationViewCtrl.showForm = false;
    this.userCreationViewCtrl.showFileChoosen = true;
    this.userCreationViewCtrl.showTable = false;
    this.excelUserList = [];
  }
  onCancelFileUploadSection() {
    this.excelFileList = null;
    this.userCreationViewCtrl.addUserBtn = false;
    this.userCreationViewCtrl.showForm = true;
    this.userCreationViewCtrl.showFileChoosen = false;
    this.userCreationViewCtrl.showTable = false;
    // this.selectedTableName = "user";

    // this.showUserbulkupload = false
    // this.showFormView = true;
    // this.hideFormBtn = false;
  }

  getBulkUserTemplate() {
    try {
      this.userService.getBulkUserTemplate().subscribe(blob => {
        importedSaveAs(blob, 'Bulk-Users-Upload-Template.xls');
      },
        error => console.log(error)
      );
    } catch (e) {
      console.log(e);
    }
  }

}
